import { Configuration, Logger, LogLevel, UserAgentApplication } from "msal";
import { autoBind } from "src/shared/decorators/autobind";
import type { CookieStorageService } from "src/shared/services/cookie-storage/cookie-storage-service";
import { Environment } from "src/libs/environment";
import { Log } from "src/libs/log";

function loggerCallback(level: LogLevel, message: string) {
    switch (level) {
        case LogLevel.Info: {
            Log.info(message);
            break;
        }
        case LogLevel.Warning: {
            Log.warn(message);
            break;
        }
        case LogLevel.Error: {
            Log.error(message);
            break;
        }
        case LogLevel.Verbose: {
            Log.normal(message);
        }
    }
}

export class Msal {
    private static cookieStorage: CookieStorageService | null = null;
    private readonly msal: UserAgentApplication | null = null;
    private readonly enabledLog =
        Environment.string("ALLOW_MSAL_LOGS") === "yes";
    private static instance = new Msal();

    static getInstance = (cookieStorageService: CookieStorageService) => {
        Msal.cookieStorage = cookieStorageService;
        return Msal.instance;
    };

    private constructor() {
        const clientId = Environment.string("MSAL_CLIENT_ID", "");
        let authority = Environment.string("MSAL_AUTHORITY", "");

        // This allow us to go directly to the register flow using a query param like this:
        // https://miespacio.pacifico.com.pe/?flujo=registrate
        const currentFlow = new URL(window.location.href).searchParams.get(
            "flujo"
        );
        const registerFlow = "registrate";
        if (currentFlow === registerFlow) {
            authority = Environment.string("MSAL_AUTHORITY_REGISTER", "");
        }

        const msalConfig: Configuration = {
            auth: {
                clientId,
                authority,
                validateAuthority: false,
            },
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false,
            },
        };
        if (this.enabledLog) {
            msalConfig.system = {
                logger: new Logger(loggerCallback, {
                    level: LogLevel.Info,
                    piiLoggingEnabled: false,
                    correlationId: "1234",
                }),
            };
        }

        try {
            this.msal = new UserAgentApplication(msalConfig);
            this.msal.handleRedirectCallback(Msal.redirectCallback);
        } catch (e) {
            Log.info(e);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private static redirectCallback(error?: Error, response?: any): void {
        if (error) {
            Log.error("Something went wrong when login in B2C");
        } else {
            Log.info("Successfully logged in B2C");
        }
    }

    @autoBind
    public login(): void {
        this.msal?.loginRedirect({
            scopes: ["openid", "profile"],
        });
    }

    @autoBind
    public logout(): void {
        localStorage.removeItem("PROMOTICK_TOKEN");
        Msal.cookieStorage?.removeCookie("PROMOTICK_AUTH_TOKEN");
        return this.msal?.logout();
    }
}
